'use client'

import { useRef } from 'react'
import cn from 'clsx'

import type { HomeHero as HomeHeroType, R2FilesStoredFile } from '@/types/sanity.types'

import { ImageBox, NavLink } from 'shared-components'

import BlockArrow from '@images/blockArrow'

import s from './HomeHero.module.sass'

export type HomeHeroProps = Omit<HomeHeroType, 'videoFile' | 'featuredPost'> & {
    featuredProject: any
    videoFile: {
        _type: string
        asset: R2FilesStoredFile
    }
}

function HomeHero({
    title,
    image,
    video,
    featuredProject,
    copy,
    videoFile
}: HomeHeroProps) {
    const homeRef = useRef<HTMLDivElement>(null)

    const scrollDown = () => {
        if (homeRef.current) {
            const scrollTo = homeRef.current.offsetTop + homeRef.current.offsetHeight - 60

            window.scrollTo({
                top: scrollTo,
                behavior: 'smooth',
            })
        }
    }

    return (
        <section className={cn(s.HomeHero, !featuredProject && s.NoProject)} ref={homeRef}>
            <div className={cn(s.Cont, 'container')}>
                <div className={s.Title}>
                    {title && <h1 dangerouslySetInnerHTML={{ __html: title }} />}
                    {featuredProject && <span className="smallCaps">Featured project</span>}
                </div>
                <div className={s.Image}>
                    {videoFile && videoFile?.asset && <video autoPlay loop muted playsInline src={videoFile?.asset?.fileURL} />}
                    {image && !video && <ImageBox image={image} priority />}
                </div>
                {featuredProject && (
                    <div className={s.Project}>
                        <NavLink href={`/our-work/${featuredProject.slug}`} className={s.ProjectInner}>
                            <span className="smallCaps">Featured project</span>
                            <div className={s.ProjectImage}>
                                {featuredProject.featuredImage && <ImageBox width={340} height={340} image={featuredProject.featuredImage} priority />}
                                {BlockArrow}
                            </div>
                            <div className={s.ProjectMain}>
                                <div>
                                    {featuredProject?.projectCategories && (
                                        <span
                                            className={s.ProjectCategory}
                                            dangerouslySetInnerHTML={{
                                                __html: featuredProject?.projectCategories?.[0]?.name,
                                            }}
                                        />
                                    )}
                                    {featuredProject.location && (
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: featuredProject.location,
                                            }}
                                        />
                                    )}
                                </div>
                                {featuredProject.excerpt && (
                                    <p
                                        className={s.ProjectExcerpt}
                                        dangerouslySetInnerHTML={{
                                            __html: `“${featuredProject.excerpt}”`,
                                        }}
                                    />
                                )}
                                {BlockArrow}
                            </div>
                        </NavLink>
                    </div>
                )}
                <div className={s.Copy}>
                    {copy && <h2 className="h5 manrope" dangerouslySetInnerHTML={{ __html: copy }} />}
                    <div className={s.Down} onClick={scrollDown}>
                        {BlockArrow}
                    </div>
                </div>
            </div>
        </section>
    )
}

export default HomeHero
